var isSetThumbnailFromMedia = false;

if (!customElements.get('media-gallery')) {
  function debounce(func, wait, immediate) {
    var timeout;
    return function () {
      var context = this,
        args = arguments;
      var later = function () {
        timeout = null;
        if (!immediate) func.apply(context, args);
      };
      var callNow = immediate && !timeout;
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
      if (callNow) func.apply(context, args);
    };
  }

  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 100));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      connectedCallback() {
        this.setupVideoAutoplay();
      }

      setupVideoAutoplay() {
        // If DOM is already loaded, initialize immediately
        if (document.readyState === 'loading') {
          window.addEventListener('DOMContentLoaded', () => {
            this.initializeVideos();
          });
        } else {
          this.initializeVideos();
        }
      }

      initializeVideos() {
        const windowWidth = window.innerWidth;

        if (windowWidth < 750) {
          // Set all images to eager load
          this.eagerLoadImages();
        }
        
        const allDeferredMedia = this.elements.viewer.querySelectorAll('.deferred-media');
        
        // Load all videos
        allDeferredMedia.forEach(deferredMedia => {
          if (deferredMedia.getAttribute('loaded')) return;
          
          const poster = deferredMedia.querySelector('.deferred-media__poster');
          if (poster) poster.style.opacity = '0';
          
          deferredMedia.loadContent(false);
        });
        
        requestAnimationFrame(() => {
          const allVideos = this.elements.viewer.querySelectorAll('.deferred-media video');
          
          allVideos.forEach(video => {
            video.muted = true;
            video.controls = true;
            video.playsInline = true;
            video.setAttribute('playsinline', '');
          });
          
          // Disconnect existing observer if it exists
          if (this.videoObserver) {
            this.videoObserver.disconnect();
          }
          
          // Play video when it comes into view and pause when it goes out of view
          this.videoObserver = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
              const video = entry.target;
              if (entry.isIntersecting) {
                video.play().catch(() => {});
              } else {
                video.pause();
              }
            });
          }, { threshold: 0.5 });
          
          allVideos.forEach(video => this.videoObserver.observe(video));
        });
      }

      eagerLoadImages() {
        const allImages = this.elements.viewer.querySelectorAll('.product__media img');
        allImages.forEach(img => {
          if (img.loading === 'lazy') {
            img.loading = 'eager';
          }
        });
      }

      onSlideChanged(event) {
        const currentElement = event.detail.currentElement;
        if (!currentElement || !currentElement.dataset) return;

        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend, shouldScroll = true) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
          
          // Reinitialize videos after media gallery update
          this.initializeVideos();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          
          // Only scroll to the media if shouldScroll is true
          if (shouldScroll) {
            const activeMediaRect = activeMedia.getBoundingClientRect();
            // Don't scroll if the image is already in view
            if (activeMediaRect.top > -0.5) return;
            const top = activeMediaRect.top + window.scrollY;
            window.scrollTo({ top: top, behavior: 'smooth' });
          }
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        
        // Only load content if it hasn't been loaded yet (to avoid reloading and buffering issues)
        if (deferredMedia && !deferredMedia.getAttribute('loaded')) {
          deferredMedia.loadContent(false);
        }
        
        // Handle autoplay-video containers (for videos that are not deferred)
        const autoplayVideo = activeItem.querySelector('.autoplay-video');
        if (autoplayVideo) {
          // Find video element (could be <video> or iframe for external videos)
          const videoElement = autoplayVideo.querySelector('video');
          const iframeElement = autoplayVideo.querySelector('iframe');
          
          if (videoElement) {
            // For HTML5 video elements
            videoElement.play().catch(error => {
              // Some browsers may block autoplay, log but don't throw
              console.log('Video autoplay prevented:', error);
            });
          } else if (iframeElement) {
            // For external videos (YouTube, Vimeo, etc.)
            // The iframe should already have autoplay parameter in the URL
            // But we can ensure it's loaded by setting src again if needed
            const currentSrc = iframeElement.src;
            if (currentSrc && !currentSrc.includes('autoplay=1')) {
              // If autoplay is not in URL, add it
              const separator = currentSrc.includes('?') ? '&' : '?';
              iframeElement.src = currentSrc + separator + 'autoplay=1&mute=1';
            }
          }
        }
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}